import * as React from 'react';

import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DvrOutlinedIcon from '@mui/icons-material/DvrOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import KeyboardArrowRightOutlinedIcon from '@mui/icons-material/KeyboardArrowRightOutlined';
import NotificationsNoneOutlinedIcon from '@mui/icons-material/NotificationsNoneOutlined';
import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import SupervisorAccountOutlinedIcon from '@mui/icons-material/SupervisorAccountOutlined';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { useTranslation } from 'react-i18next';

import { screenType } from '../../constants/enum';

import type * as authType from '../../api/firebase/auth/authType';

const styles = {
  button: {
    '&.Mui-disabled': {
      '.MuiListItemText-root': {
        '.MuiTypography-root': {
          fontWeight: '700'
        }
      },
      backgroundColor: 'rgba(255, 255, 255, 0.24)',
      borderRadius: '4px',
      opacity: 1
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.24)',
      borderRadius: '4px'
    },
    height: '40px',
    padding: '8px',
    width: '240px'
  },
  buttonText: {
    '.MuiTypography-root': {
      fontSize: '14px'
    },
    color: '#FFF'
  },
  icon: {
    color: '#FFF',
    width: '24px'
  },
  item: {
    padding: '8px'
  },
  link: {
    textDecoration: 'none'
  },
  list: {
    padding: '0px'
  }
};

const SidebarLinks: React.FC<authType.RegisterScreenProps> = (props) => {
  const { t } = useTranslation();

  const buttonLists = {
    Publisher: [
      { text: t('sidebar.home'), url: `/main/publisher/mypage` },
      { text: t('sidebar.company'), url: `/main/publisher/mypage/company` },
      {
        text: t('sidebar.publisherMedia'),
        url: `/main/publisher/mypage/media`
      },
      { text: t('sidebar.article'), url: `/main/publisher/mypage/article` },
      { text: t('sidebar.staff'), url: `/main/publisher/mypage/staff` },
      {
        text: t('sidebar.publisherIssue'),
        url: `/main/publisher/mypage/issue`
      },
      {
        text: t('sidebar.favoriteIssue'),
        url: `/main/publisher/mypage/issue/favorite`
      },
      { text: t('sidebar.message'), url: `/main/publisher/mypage/message` },
      { text: t('sidebar.news'), url: `/main/publisher/mypage/news` }
    ],
    agent: [
      { text: t('sidebar.home'), url: `/main/agent/mypage` },
      { text: t('sidebar.company'), url: `/main/agent/mypage/company` },
      { text: t('sidebar.client'), url: `/main/agent/mypage/client` },
      { text: t('sidebar.issue'), url: `/main/agent/mypage/issue` },
      { text: t('sidebar.staff'), url: `/main/agent/mypage/staff` },
      { text: t('sidebar.message'), url: `/main/agent/mypage/message` },
      { text: t('sidebar.media'), url: `/main/agent/mypage/media` },
      {
        text: t('sidebar.favoriteMedia'),
        url: `/main/agent/mypage/media/favorite`
      },
      { text: t('sidebar.news'), url: `/main/agent/mypage/news` }
    ],
    client: [
      { text: t('sidebar.home'), url: `/main/client/mypage` },
      { text: t('sidebar.company'), url: `/main/client/mypage/company` },
      { text: t('sidebar.issue'), url: `/main/client/mypage/issue` },
      { text: t('sidebar.staff'), url: `/main/client/mypage/staff` },
      { text: t('sidebar.message'), url: `/main/client/mypage/message` },
      { text: t('sidebar.media'), url: `/main/client/mypage/media` },
      {
        text: t('sidebar.favoriteMedia'),
        url: `/main/client/mypage/media/favorite`
      },
      { text: t('sidebar.news'), url: `/main/client/mypage/news` }
    ]
  };

  const currentPage = location.pathname;
  const issueUrl = `/mypage/issue`;
  const favoriteIssueUrl = `/mypage/issue/favorite`;
  const orientationUrl = `/mypage/orientation/edit`;
  const issueCompleteUrl = `/mypage/issue/complete`;
  const companyEditUrl = `/mypage/company/edit`;
  const staffUrl = `/mypage/staff`;
  const mediaUrl = `/mypage/media`;
  const favoriteMediaUrl = `/mypage/media/favorite`;
  const newsUrl = `/mypage/news`;
  const article = `/mypage/article`;
  const message = 'mypage/message';

  let buttonList;
  if (props.screenType === screenType.client) {
    buttonList = buttonLists.client;
  } else if (props.screenType === screenType.agent) {
    buttonList = buttonLists.agent;
  } else {
    buttonList = buttonLists.Publisher;
  }

  const iconSwitcher = (text: string): JSX.Element => {
    switch (text) {
      case t('sidebar.home'):
        return <HomeOutlinedIcon sx={styles.icon} />;
      case t('sidebar.company'):
        return <AccountCircleOutlinedIcon sx={styles.icon} />;
      case t('sidebar.issue'):
      case t('sidebar.publisherIssue'):
        return <DescriptionOutlinedIcon sx={styles.icon} />;
      case t('sidebar.media'):
      case t('sidebar.publisherMedia'):
        return <DvrOutlinedIcon sx={styles.icon} />;
      case t('sidebar.favoriteIssue'):
      case t('sidebar.favoriteMedia'):
        return <StarBorderOutlinedIcon sx={styles.icon} />;
      case t('sidebar.client'):
        return <BusinessOutlinedIcon sx={styles.icon} />;
      case t('sidebar.staff'):
        return <SupervisorAccountOutlinedIcon sx={styles.icon} />;
      case t('sidebar.message'):
        return <ChatBubbleOutlineOutlinedIcon sx={styles.icon} />;
      case t('sidebar.article'):
        return <EditOutlinedIcon sx={styles.icon} />;
      case t('sidebar.news'):
        return <NotificationsNoneOutlinedIcon sx={styles.icon} />;
      default:
        return <></>;
    }
  };

  const focusSwitcher = (buttonUrl: string): boolean => {
    if (
      buttonUrl.includes(`mypage/issue`) &&
      !buttonUrl.includes(`mypage/issue/favorite`)
    ) {
      return (
        (currentPage.includes(issueUrl) &&
          !currentPage.includes(favoriteIssueUrl)) ||
        (currentPage.includes(orientationUrl) &&
          !currentPage.includes(favoriteIssueUrl)) ||
        (currentPage.includes(issueCompleteUrl) &&
          !currentPage.includes(favoriteIssueUrl))
      );
    } else if (buttonUrl.includes(`mypage/company`)) {
      return currentPage.includes(companyEditUrl);
    } else if (buttonUrl.includes(`mypage/staff`)) {
      return currentPage.includes(staffUrl);
    } else if (
      buttonUrl.includes(`mypage/media`) &&
      !buttonUrl.includes(favoriteMediaUrl)
    ) {
      return (
        currentPage.includes(mediaUrl) &&
        !currentPage.includes(favoriteMediaUrl)
      );
    } else if (buttonUrl.includes(`mypage/news`)) {
      return currentPage.includes(newsUrl);
    } else if (buttonUrl.includes(`mypage/article`)) {
      return currentPage.includes(article);
    } else if (buttonUrl.includes('mypage/message')) {
      return currentPage.includes(message);
    }
    return false;
  };

  return (
    <nav>
      <List sx={styles.list}>
        {buttonList.map((button, index) => (
          <ListItem key={index} sx={styles.item}>
            <ListItemButton
              sx={{
                ...styles.button,
                ...(currentPage === button.url || focusSwitcher(button.url)
                  ? {
                      backgroundColor: 'rgba(255, 255, 255, 0.24)',
                      borderRadius: '4px'
                    }
                  : {})
              }}
              href={button.url}
            >
              <ListItemIcon sx={{ minWidth: '48px' }}>
                {iconSwitcher(button.text)}
              </ListItemIcon>
              <ListItemText primary={button.text} sx={styles.buttonText} />
              <KeyboardArrowRightOutlinedIcon sx={styles.icon} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </nav>
  );
};

export default SidebarLinks;
